import { useCustom, useTranslate } from '@refinedev/core';
import { Alert, Button, Radio, Space, Table, Tag, Typography } from 'antd';
import { format, parseISO } from 'date-fns';
import { enUS, ru } from 'date-fns/locale';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PaperPhotoViewer } from '../homework/PaperPhotoViewer';
import { WorksheetFillModal } from './WorksheetFillModal';
import {
  CLIENT_WORKSHEET_FIELDS,
  HUB_FILTERS,
  HUB_LEGACY_FILTERS,
  canFillWithClient,
  canOpenResponse,
  type ClientWorksheetRow,
  type ClientWorksheetStatus,
  type HubFilter,
} from './hubModel';

/**
 * E34 · ClientWorksheets — "Client Hub — Worksheets & documents" (cc-homework, chapter "Worksheets —
 * Responses & documents (portal, E34)", buildOrder 14): the client's forms in one place, each with its
 * document status — sent, viewed, submitted, signed — so "who has not filled the intake?" is one look
 * (PM-03's pending-document tracking). Open shows the response; Fill with the client is its section
 * (SubmitWorksheet in session). Reads `clientWorksheets(subject, filter)` (the inline read model — a
 * submit or a signature is read back at once).
 *
 * Behavior lines implemented here:
 *   · filter: all · pending · the five purpose categories · intake · consent (homework only for history) — the read model's own filter argument (a
 *     filter is a query, never a command); the chips count from one read of everything;
 *   · a consent submitted and not yet signed is PENDING and reads "Awaiting signature";
 *   · a form the therapist filled with the client says so ("Filled in session");
 *   · a client's own journal is listed read-only — no Fill with the client (it is theirs alone);
 *   · a scored template shows its score;
 *   · a worksheet done on paper (Q-paper-2 A) reads "Done on paper" with a "photo" link in place of Submitted at,
 *     opening the paper-photo viewer — there is no response to open.
 */
type Props = {
  clientId: string;
  clientName: string;
  locale: 'en' | 'ru';
};

const STATUS_COLOR: Record<ClientWorksheetStatus, string> = {
  sent: 'default',
  viewed: 'blue',
  submitted: 'green',
  signed: 'green',
  paper: 'green',
  cancelled: 'default',
};

export const ClientWorksheetsTab = ({ clientId, clientName, locale }: Props) => {
  const t = useTranslate();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<HubFilter>('all');
  const [filling, setFilling] = useState<string | null>(null);
  // Q-paper-2 A: the photo of a sheet done on paper, in the Homework tab's viewer
  const [photo, setPhoto] = useState<{ assignmentId: string; fileRef: string } | null>(null);

  const read = (f: HubFilter) => ({
    url: '',
    method: 'get' as const,
    errorNotification: false as const,
    meta: {
      operation: 'clientWorksheets',
      fields: CLIENT_WORKSHEET_FIELDS,
      variables: {
        subject: { name: 'subject', type: 'ID!', value: clientId },
        filter: { name: 'filter', type: 'String', value: f },
      },
    },
    queryOptions: { enabled: Boolean(clientId) },
  });
  const all = useCustom<ClientWorksheetRow[]>(read('all'));
  const shownQuery = useCustom<ClientWorksheetRow[]>(read(filter));
  const everything = useMemo(() => all.data?.data ?? [], [all.data]);
  const shown = useMemo(() => shownQuery.data?.data ?? [], [shownQuery.data]);
  const isError = all.isError || shownQuery.isError;

  // D-entries-over-time-2026-10-07: a REPEATABLE worksheet offers "Over time" (the template's own flag, read from the
  // library query the Assign drawer already uses — the hub row does not carry it; a retired template is not listed)
  const templates = useCustom<{ templateId: string; repeatable: boolean }[]>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'worksheetTemplates',
      fields: ['templateId', 'repeatable'],
      variables: { language: { name: 'language', type: 'String', value: null } },
    },
  });
  const repeatable = useMemo(
    () => new Set((templates.data?.data ?? []).filter((x) => x.repeatable).map((x) => x.templateId)),
    [templates.data],
  );
  const listed = useMemo(
    () => new Set((templates.data?.data ?? []).map((x) => x.templateId)),
    [templates.data],
  );

  const count = (f: HubFilter) =>
    f === 'pending'
      ? everything.filter((r) => r.pending).length
      : f === 'all'
        ? everything.length
        : everything.filter((r) => r.category === f).length;

  const dateLocale = locale === 'ru' ? ru : enUS;
  const when = (iso: string | null | undefined) =>
    iso ? format(parseISO(iso), 'd MMM yyyy, HH:mm', { locale: dateLocale }) : '—';

  const refetch = () => {
    all.refetch();
    shownQuery.refetch();
  };

  return (
    <Space
      direction="vertical"
      size="middle"
      style={{ width: '100%' }}
      data-testid="worksheets-hub"
    >
      <div>
        <Typography.Title level={5} style={{ margin: 0 }}>
          {t('worksheets.hub.title')}
        </Typography.Title>
        <Typography.Text type="secondary">{t('worksheets.hub.intro')}</Typography.Text>
      </div>

      <div>
        <Radio.Group
          optionType="button"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          data-testid="worksheets-filter"
        >
          {HUB_FILTERS.filter((f) => !HUB_LEGACY_FILTERS.includes(f) || count(f) > 0).map((f) => (
            <Radio.Button key={f} value={f} data-testid={`worksheets-filter-${f}`}>
              {t(`worksheets.hub.filter.${f}`)}
              {f === 'all' ? '' : ` · ${count(f)}`}
            </Radio.Button>
          ))}
        </Radio.Group>
      </div>

      {isError ? (
        <Alert type="error" showIcon message={t('worksheets.hub.loadFailed')} />
      ) : null}

      <Table<ClientWorksheetRow>
        rowKey="assignmentId"
        size="middle"
        loading={shownQuery.isFetching}
        dataSource={shown}
        pagination={false}
        tableLayout="fixed"
        scroll={{ x: 1000 }}
        locale={{ emptyText: t('worksheets.hub.empty') }}
        data-testid="worksheets-table"
        onRow={(row) =>
          ({
            'data-testid': `worksheets-row-${row.assignmentId}`,
            'data-status': row.status,
            'style': row.status === 'cancelled' ? { opacity: 0.55 } : undefined,
          } as never)
        }
        columns={[
          {
            title: t('worksheets.hub.column.document'),
            width: 250,
            render: (_, row) => (
              <Space direction="vertical" size={0}>
                <Typography.Text strong data-testid={`worksheets-name-${row.assignmentId}`}>
                  {row.name ?? row.templateId}
                </Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {[
                    row.category ? t(`worksheets.category.${row.category}`) : null,
                    row.templateVersion
                      ? t('worksheets.hub.version', { version: row.templateVersion })
                      : null,
                    row.submissionIds.length > 1
                      ? t('worksheets.hub.entries', { count: row.submissionIds.length })
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography.Text>
                {row.selfAssigned ? (
                  <Typography.Text
                    type="secondary"
                    style={{ fontSize: 12 }}
                    data-testid={`worksheets-self-${row.assignmentId}`}
                  >
                    {t('worksheets.hub.selfSet')}
                  </Typography.Text>
                ) : null}
              </Space>
            ),
          },
          {
            title: t('worksheets.hub.column.status'),
            width: 170,
            render: (_, row) => (
              <Space direction="vertical" size={2}>
                <Tag
                  color={STATUS_COLOR[row.status]}
                  data-testid={`worksheets-status-${row.assignmentId}`}
                >
                  {t(`worksheets.hub.status.${row.status}`)}
                </Tag>
                {row.awaitingSignature ? (
                  <Tag color="orange" data-testid={`worksheets-awaiting-${row.assignmentId}`}>
                    {t('worksheets.hub.awaitingSignature')}
                  </Tag>
                ) : null}
                {row.filledInSession ? (
                  <Tag data-testid={`worksheets-session-${row.assignmentId}`}>
                    {t('worksheets.hub.inSession')}
                  </Tag>
                ) : null}
              </Space>
            ),
          },
          {
            title: t('worksheets.hub.column.sent'),
            width: 150,
            render: (_, row) => <span data-testid={`worksheets-sent-${row.assignmentId}`}>{when(row.sentAt)}</span>,
          },
          {
            title: t('worksheets.hub.column.viewed'),
            width: 150,
            render: (_, row) => <span data-testid={`worksheets-viewed-${row.assignmentId}`}>{when(row.viewedAt)}</span>,
          },
          {
            title: t('worksheets.hub.column.submitted'),
            width: 150,
            render: (_, row) =>
              row.status === 'paper' && row.paperPhoto ? (
                // Q-paper-2 A: "photo" in place of Submitted at — there is no response, the answers are on the sheet
                <Button
                  size="small"
                  type="link"
                  style={{ padding: 0 }}
                  onClick={() =>
                    setPhoto({ assignmentId: row.assignmentId, fileRef: (row.paperPhoto as { fileRef: string }).fileRef })
                  }
                  data-testid={`worksheets-paper-photo-${row.assignmentId}`}
                >
                  {t('worksheets.hub.paperPhoto')}
                </Button>
              ) : (
                <span data-testid={`worksheets-submitted-${row.assignmentId}`}>{when(row.submittedAt)}</span>
              ),
          },
          {
            title: t('worksheets.hub.column.signed'),
            width: 150,
            render: (_, row) => <span data-testid={`worksheets-signed-${row.assignmentId}`}>{when(row.signedAt)}</span>,
          },
          {
            title: t('worksheets.hub.column.score'),
            width: 80,
            render: (_, row) => (
              <span data-testid={`worksheets-score-${row.assignmentId}`}>
                {row.score === null || row.score === undefined ? '—' : row.score}
              </span>
            ),
          },
          {
            title: t('worksheets.hub.column.actions'),
            width: 200,
            render: (_, row) => (
              <Space size={0} wrap>
                {canOpenResponse(row) ? (
                  <Button
                    size="small"
                    type="link"
                    onClick={() =>
                      navigate(`/worksheets/response/${encodeURIComponent(row.submissionId as string)}`)
                    }
                    data-testid={`worksheets-open-${row.assignmentId}`}
                  >
                    {t('worksheets.hub.open')}
                  </Button>
                ) : null}
                {templates.isSuccess &&
                (listed.has(row.templateId) ? (
                  repeatable.has(row.templateId)
                ) : null) !== false ? (
                  <OverTimeButton
                    row={row}
                    knownRepeatable={listed.has(row.templateId) ? true : null}
                    clientName={clientName}
                  />
                ) : null}
                {canFillWithClient(row) ? (
                  <Button
                    size="small"
                    type="link"
                    onClick={() => setFilling(row.assignmentId)}
                    data-testid={`worksheets-fill-${row.assignmentId}`}
                  >
                    {t('worksheets.hub.fill')}
                  </Button>
                ) : null}
              </Space>
            ),
          },
        ]}
      />

      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {t('worksheets.hub.legend')}
      </Typography.Text>

      <PaperPhotoViewer photo={photo} onClose={() => setPhoto(null)} />

      <WorksheetFillModal
        assignmentId={filling}
        clientName={clientName}
        onClose={() => setFilling(null)}
        onDone={() => {
          setFilling(null);
          refetch();
        }}
      />
    </Space>
  );
};

/**
 * "Over time" on a REPEATABLE worksheet. The active library list answers for the templates it holds; a template
 * the practice has since RETIRED is not listed, so its flag is read by id (`worksheetTemplate` — a retired template
 * stays readable), once per such template — a client's journal never loses its Over time because the form was retired.
 */
const OverTimeButton = ({
  row,
  knownRepeatable,
  clientName,
}: {
  row: ClientWorksheetRow;
  knownRepeatable: true | null;
  clientName: string;
}) => {
  const t = useTranslate();
  const navigate = useNavigate();
  const byId = useCustom<{ repeatable: boolean }>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'worksheetTemplate',
      fields: ['templateId', 'repeatable'],
      variables: { templateId: { name: 'templateId', type: 'ID!', value: row.templateId } },
    },
    queryOptions: { enabled: knownRepeatable === null, retry: false },
  });
  if (knownRepeatable === null && !byId.data?.data?.repeatable) return null;
  return (
    <Button
      size="small"
      type="link"
      onClick={() =>
        navigate(
          `/worksheets/over-time/${encodeURIComponent(row.subject)}/${encodeURIComponent(row.templateId)}`,
          { state: { clientName } },
        )
      }
      data-testid={`worksheets-over-time-${row.assignmentId}`}
    >
      {t('worksheets.overTime.entry')}
    </Button>
  );
};
